/**
 * Заглушка вместо карты: нет ключа MapGL или скрипт не загрузился.
 * Список шагов при этом остаётся полностью рабочим — карта здесь не главное.
 */

import { useState, type FormEvent } from 'react';

import type { LatLon } from '@cdf/shared';

interface MapPlaceholderProps {
  title: string;
  text: string;
  point: LatLon | null;
  /** Ручной ввод координат — единственный способ задать старт без карты. */
  onPick(point: LatLon): void;
  /** Форма открылась: экран может убрать шторку, чтобы её не перекрывать. */
  onOpenForm?(): void;
}

export function MapPlaceholder({
  title,
  text,
  point,
  onPick,
  onOpenForm,
}: MapPlaceholderProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState(point ? String(point.lat) : '');
  const [lon, setLon] = useState(point ? String(point.lon) : '');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const rawLat = lat.trim().replace(',', '.');
    const rawLon = lon.trim().replace(',', '.');

    // Пустое поле обязательно отсекать отдельно: Number('') равен нулю и
    // проходит все проверки диапазона. Незаполненная форма отправляла старт
    // в точку 0,0 — в Гвинейский залив, где ни машин, ни заправок.
    if (rawLat === '' || rawLon === '') {
      setError('Заполните обе координаты');
      return;
    }

    const parsedLat = Number(rawLat);
    const parsedLon = Number(rawLon);
    if (
      !Number.isFinite(parsedLat) ||
      !Number.isFinite(parsedLon) ||
      Math.abs(parsedLat) > 90 ||
      Math.abs(parsedLon) > 180
    ) {
      setError('Проверьте координаты: широта −90…90, долгота −180…180');
      return;
    }
    setError(null);
    onPick({ lat: parsedLat, lon: parsedLon });
    setOpen(false);
  };

  return (
    <div className="mapstub">
      <div className="mapstub__grid" aria-hidden="true" />
      <div className="mapstub__body">
        <div className="mapstub__icon" aria-hidden="true">
          🗺
        </div>
        <h2 className="mapstub__title">{title}</h2>
        <p className="mapstub__text">{text}</p>

        {point ? (
          <p className="mapstub__coords num">
            Старт: {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
          </p>
        ) : null}

        {open ? (
          <form className="mapstub__form" onSubmit={submit}>
            <label className="field field--compact">
              <span className="field__label">Широта</span>
              <input
                className="field__input"
                inputMode="decimal"
                value={lat}
                onChange={(event) => setLat(event.target.value)}
                placeholder="55.75222"
              />
            </label>
            <label className="field field--compact">
              <span className="field__label">Долгота</span>
              <input
                className="field__input"
                inputMode="decimal"
                value={lon}
                onChange={(event) => setLon(event.target.value)}
                placeholder="37.61556"
              />
            </label>
            {error ? (
              <p className="field__error" role="alert">
                {error}
              </p>
            ) : null}
            <button type="submit" className="btn btn--primary btn--block">
              Задать точку
            </button>
          </form>
        ) : (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              // Подставляем уже известную точку: без этого поля выглядят
              // заполненными за счёт подсказки, но по факту пусты.
              if (point) {
                setLat(String(point.lat));
                setLon(String(point.lon));
              }
              setError(null);
              setOpen(true);
              onOpenForm?.();
            }}
          >
            Ввести координаты вручную
          </button>
        )}
      </div>
    </div>
  );
}
